/**
 * Scan-target builder (P4, 2026-06-16) - build connector-targets.json from a
 * form so operators never hand-write JSON ("not everyone understands JSON").
 * All logic is in @/lib/connector-targets (unit-tested); this is the UI shell.
 */
import { Rx } from '@/lib/regulation-terms';
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Copy, Download, FileJson, Plus, Trash2, Server, ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { GlassCard } from '@/components/ui/card';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { serializeTargets, TARGET_SPECS, type TargetKind, type TargetRow } from '@/lib/connector-targets';

const KINDS = Object.keys(TARGET_SPECS) as TargetKind[];
const inputCls = 'w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-primary/50';

export function DiscoveryConnectorBuilder() {
  const [rows, setRows] = useState<TargetRow[]>([{ kind: 'smb_server', fields: {} }]);
  const json = useMemo(() => serializeTargets(rows), [rows]);
  const count = useMemo(() => {
    try {
      return (JSON.parse(json) as unknown[]).length;
    } catch {
      return 0;
    }
  }, [json]);

  const setKind = (i: number, kind: TargetKind) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { kind, fields: {} } : row)));
  const setField = (i: number, key: string, val: string) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, fields: { ...row.fields, [key]: val } } : row)));
  const addRow = () => setRows((r) => [...r, { kind: 'smb_server', fields: {} }]);
  const removeRow = (i: number) => setRows((r) => (r.length > 1 ? r.filter((_, idx) => idx !== i) : r));

  const copy = () => void navigator.clipboard?.writeText(json);
  const download = () => {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'connector-targets.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Scan targets"
        subtitle="Build connector-targets.json with a form - no hand-written JSON. Add file servers, folders, or databases, then download the file for the scanner."
        badges={[{ label: `${count} target(s)`, variant: 'success' }]}
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <GlassCard className="p-5 space-y-4">
          {rows.map((row, i) => {
            const spec = TARGET_SPECS[row.kind];
            return (
              <div key={i} className="rounded-lg border border-white/10 p-4 space-y-3" data-testid="target-row">
                <div className="flex items-center gap-2">
                  <select
                    className={inputCls}
                    value={row.kind}
                    aria-label="Target type"
                    onChange={(e) => setKind(i, e.target.value as TargetKind)}
                  >
                    {KINDS.map((k) => (
                      <option key={k} value={k} className="bg-slate-900 text-slate-100">
                        {TARGET_SPECS[k].label}
                      </option>
                    ))}
                  </select>
                  <Button size="sm" variant="glass" onClick={() => removeRow(i)} aria-label="Remove target">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {[...spec.required, ...spec.optional].map((key) => (
                  <input
                    key={key}
                    className={inputCls}
                    placeholder={`${key}${spec.required.includes(key) ? ' (required)' : ' (optional)'}`}
                    type={key === 'password' ? 'password' : 'text'}
                    value={row.fields[key] ?? ''}
                    onChange={(e) => setField(i, key, e.target.value)}
                  />
                ))}
              </div>
            );
          })}
          <Button size="sm" variant="glass" onClick={addRow}>
            <Plus className="h-4 w-4 mr-1" /> Add target
          </Button>
        </GlassCard>

        <GlassCard className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <FileJson className="h-4 w-4" /> connector-targets.json
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="glass" onClick={copy}>
                <Copy className="h-4 w-4 mr-1" /> Copy
              </Button>
              <Button size="sm" onClick={download} data-action="disc.dspm_targets.a01">
                <Download className="h-4 w-4 mr-1" /> Download
              </Button>
            </div>
          </div>
          <pre className="text-xs bg-black/30 rounded-lg p-3 overflow-auto max-h-[60vh] whitespace-pre" data-testid="json-preview">
            {json}
          </pre>
        </GlassCard>
      </div>

      {/* Shortcut to the environment-aware scanner deployment. The targets
          above say WHAT to scan; this is HOW to get a scanner running where
          the data lives. The full wizard lives under Settings & Admin ->
          Provision; this CTA makes it reachable from the scan-setup flow. */}
      <GlassCard className="mt-6 p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="rounded-lg bg-primary/10 p-2.5 shrink-0">
            <Server className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="text-sm font-semibold">Next: deploy the scanner for your environment</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Pick where it runs - Windows, Linux, macOS, Docker or Kubernetes - and download a
              ready-to-run installer with a short how-to note. Endpoint agents (laptops and file
              servers) auto-detect <Rx>PII</Rx> in PDF, Excel, CSV and text - PAN, Aadhaar, IMEI, email and
              more - and send finding metadata only; file contents never leave the device.
            </p>
          </div>
        </div>
        <Link to="/admin/edge-provisioning" className="shrink-0">
          <Button size="sm">
            Deploy a scanner
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </Link>
      </GlassCard>
    </PageContainer>
  );
}
